import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { ClipboardCheck, PenTool } from 'lucide-react';
import SignOffReadinessMergedCard from './SignOffReadinessMergedCard';
import ESignature from '../../ESignature';
import { useAccountantAuth } from '../../../contexts/AccountantAuthContext';
import { api } from '../../../utils/api';

export interface SignOffReviewContentProps {
  reportId?: string;
  showHeader?: boolean;
}

type SignOffSubTab = 'readiness' | 'signature';

const SUB_TABS: { id: SignOffSubTab; label: string; icon: React.ElementType }[] = [
  { id: 'readiness', label: 'Readiness', icon: ClipboardCheck },
  { id: 'signature', label: 'Signature', icon: PenTool },
];

export function SignOffReviewContent({
  reportId: reportIdProp,
  showHeader = true,
}: SignOffReviewContentProps) {
  const { reportId: reportIdParam } = useParams<{ reportId: string }>();
  const reportId = reportIdProp ?? reportIdParam;
  const { getAuthHeader } = useAccountantAuth();
  const [subTab, setSubTab] = useState<SignOffSubTab>('readiness');

  // Sign-off readiness slice — fetched once for both tabs
  const [payload, setPayload] = useState<any | null>(null);
  const [payloadLoading, setPayloadLoading] = useState(false);

  const fetchPayload = () => {
    if (!reportId) return;
    const controller = new AbortController();
    setPayloadLoading(true);
    api
      .get(`/api/v1/dashboard/accountant-payload/${reportId}`, {
        signal: controller.signal,
        showErrorToast: false,
      })
      .then((data: any) => {
        if (controller.signal.aborted) return;
        setPayload(data || null);
      })
      .catch(() => setPayload(null))
      .finally(() => setPayloadLoading(false));
    return () => controller.abort();
  };

  useEffect(() => {
    return fetchPayload();
  }, [reportId]);

  const signOffReady = payload?.sign_off_ready === true;
  const signOffBlockers: string[] = payload?.sign_off_blockers ?? [];

  return (
    <div className="p-6 lg:p-8 max-w-[1200px] mx-auto">
      {showHeader && (
        <div className="mb-6">
          <div className="cs-eyebrow flex items-center gap-2 mb-1">
            <span>ACCOUNTANT REVIEW</span>
            <span>•</span>
            <span>SIGN-OFF</span>
          </div>
          <h1 className="cs-display">Sign-Off · Attest</h1>
        </div>
      )}

      {/* Segmented sub-nav */}
      <div
        role="tablist"
        aria-label="Sign-off sections"
        className="flex items-center gap-1 mb-6"
        style={{ borderBottom: '1px solid var(--color-hairline)' }}
      >
        {SUB_TABS.map(({ id, label, icon: Icon }) => {
          const active = subTab === id;
          return (
            <button
              key={id}
              role="tab"
              aria-selected={active}
              onClick={() => setSubTab(id)}
              data-testid={`signoff-subtab-${id}`}
              className="cs-label flex items-center gap-2 px-3 py-2 rounded-[2px] transition-colors"
              style={{
                color: active ? 'var(--color-text-secondary)' : 'var(--color-text-dim)',
                borderBottom: active ? '2px solid var(--color-brand)' : '2px solid transparent',
                marginBottom: '-1px',
              }}
            >
              <Icon className="w-4 h-4" />
              {label}
            </button>
          );
        })}
      </div>

      <div data-testid={`signoff-panel-${subTab}`}>
        {subTab === 'readiness' && reportId && (
          <SignOffReadinessMergedCard
            caseId={reportId}
            signOffReady={signOffReady}
            signOffBlockers={signOffBlockers}
            onSignedOff={() => {
              // Refresh payload to update sign-off state across tabs
              fetchPayload();
            }}
          />
        )}
        {subTab === 'signature' && reportId && (
          <div className="space-y-4">
            {!signOffReady && !payloadLoading && (
              <div
                className="rounded-[2px] px-4 py-3 text-sm"
                style={{
                  borderLeft: '2px solid var(--color-blocker)',
                  backgroundColor: 'var(--color-bg-elevated)',
                }}
                data-testid="signoff-signature-gated"
              >
                <p style={{ color: 'var(--color-text-secondary)' }}>
                  Signing is locked until the case is ready.
                  {signOffBlockers.length > 0
                    ? ` ${signOffBlockers.length} blocker${signOffBlockers.length !== 1 ? 's' : ''} must be cleared first.`
                    : ' Resolve the open items first.'}
                </p>
                <div className="mt-3 flex items-center gap-4">
                  <button
                    type="button"
                    disabled
                    aria-disabled="true"
                    data-testid="signoff-sign-disabled"
                    title="Resolve the blockers on the Readiness tab to enable signing"
                    className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium cursor-not-allowed"
                    style={{
                      borderRadius: 2,
                      backgroundColor: 'var(--color-brand)',
                      color: 'var(--color-on-brand)',
                      opacity: 0.4,
                    }}
                  >
                    <PenTool className="w-4 h-4" aria-hidden="true" />
                    Sign &amp; lock report
                  </button>
                  <button
                    type="button"
                    onClick={() => setSubTab('readiness')}
                    data-testid="signoff-view-blockers"
                    className="text-sm underline"
                    style={{ color: 'var(--color-text-secondary)' }}
                  >
                    View {signOffBlockers.length > 0 ? `${signOffBlockers.length} ` : ''}
                    blocker{signOffBlockers.length !== 1 ? 's' : ''} →
                  </button>
                </div>
              </div>
            )}
            <ESignature reportId={reportId} getAuthHeader={getAuthHeader} canSign={signOffReady} />
          </div>
        )}
      </div>
    </div>
  );
}

export default SignOffReviewContent;
